import React from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Avatar,
  Divider
} from '@mui/material';
import {
  Close as CloseIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  GpsFixed as GpsFixedIcon
} from '@mui/icons-material';
import { internType } from '../data/interntype-data';
import { optionsBranch } from '../data/branch-data';

// ฟังก์ชันแปลงระยะทางเป็นช่วง (ต้องเหมือนกับใน CoopCompanyModal)
const getDistanceRange = (distance) => {
  if (!distance) return { text: 'ไม่ทราบ', color: '#757575', bgcolor: '#f5f5f5' };
  
  if (distance < 50) return { text: '< 50 km', color: '#2e7d32', bgcolor: '#e8f5e9' };
  if (distance < 100) return { text: '50-100 km', color: '#388e3c', bgcolor: '#e8f5e9' };
  if (distance < 200) return { text: '100-200 km', color: '#689f38', bgcolor: '#f1f8e9' };
  if (distance < 300) return { text: '200-300 km', color: '#fbc02d', bgcolor: '#fffde7' };
  if (distance < 400) return { text: '300-400 km', color: '#ff8f00', bgcolor: '#fff8e1' };
  return { text: '> 400 km', color: '#d32f2f', bgcolor: '#ffebee' };
};

// ฟังก์ชันแปลงประเภทสหกิจ
const getInternTypeLabel = (typeId) => {
  const type = internType.find(t => t.value == typeId);
  return type ? type.label : 'ไม่ระบุ';
};

// ฟังก์ชันแปลงสาขาวิชา
const getBranchLabel = (branchId) => {
  const branch = optionsBranch.find(b => b.value == branchId);
  return branch ? branch.label : 'ไม่ระบุสาขา';
};

const StudentListDrawer = ({ open, onClose, selectedCompanyStudents }) => {
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { 
          width: '500px', 
          maxWidth: '90vw',
          zIndex: 1400 // สูงกว่า Modal (Material-UI Dialog default = 1300)
        }
      }}
      ModalProps={{
        style: { zIndex: 1400 } // ให้ Modal wrapper ของ Drawer อยู่เหนือ Dialog Modal
      }}
    >
      <Box sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" component="div">
            รายชื่อนิสิตสหกิจศึกษา
          </Typography>
          <IconButton 
            onClick={onClose}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {selectedCompanyStudents && (
          <>
            <Box sx={{ mb: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <BusinessIcon sx={{ color: '#1976d2' }} />
                <Typography variant="subtitle1" fontWeight="bold">
                  {selectedCompanyStudents.company.name}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                รหัส: {selectedCompanyStudents.company.id} | จังหวัด: {selectedCompanyStudents.company.province}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedCompanyStudents.company.address} ต.{selectedCompanyStudents.company.subdistrict} อ.{selectedCompanyStudents.company.district}
              </Typography>
              {selectedCompanyStudents.company.distance && (
                <Box display="flex" alignItems="center" gap={1} mt={1}>
                  <GpsFixedIcon sx={{ fontSize: 16, color: '#4caf50' }} />
                  <Typography variant="body2" color="text.secondary">
                    ระยะทาง: {getDistanceRange(selectedCompanyStudents.company.distance).text}
                  </Typography>
                </Box>
              )}
            </Box>

            <Divider sx={{ mb: 2 }} />

            <Typography variant="subtitle2" gutterBottom>
              นิสิตสหกิจศึกษา ({selectedCompanyStudents.students.length} คน)
            </Typography>

            <List>
              {selectedCompanyStudents.students.map((student, index) => (
                <ListItem 
                  key={student.id || index}
                  sx={{ 
                    border: '1px solid #e0e0e0',
                    borderRadius: 1,
                    mb: 1,
                    backgroundColor: '#fafafa'
                  }}
                >
                  <Avatar 
                    sx={{ 
                      bgcolor: '#1976d2', 
                      mr: 2,
                      width: 50,
                      height: 50
                    }}
                    src={`https://reg.buu.ac.th/registrar/getstudentimage.asp?id=${student.id}`}
                    alt={`${student.firstname} ${student.lastname}`}
                  >
                    <PersonIcon />
                  </Avatar>
                  <ListItemText
                    primary={
                      <Typography variant="body1" fontWeight="medium">
                        {student.name || `${student.firstname} ${student.lastname}`}
                      </Typography>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          รหัสนิสิต: {student.id || student.st_id}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          สาขาวิชา: {getBranchLabel(student.b_id)}
                        </Typography>
                        {student.gpa && (
                          <Typography variant="body2" color="text.secondary">
                            เกรดเฉลี่ย: {student.gpa}
                          </Typography>
                        )}
                        {student.internDetail && (
                          <Typography variant="body2" color="text.secondary">
                            ประเภท: {getInternTypeLabel(student.internDetail.type_internship)}
                          </Typography>
                        )}
                        {student.internDetail && (
                          <Typography variant="body2" color="primary" sx={{ fontSize: '0.85rem', mt: 0.5 }}>
                            {(student.internDetail.startdate || student.internDetail.start_date) && 
                             (student.internDetail.enddate || student.internDetail.end_date) ? (
                              `ช่วงเวลาฝึกงาน: ${new Date(student.internDetail.startdate || student.internDetail.start_date).toLocaleDateString('th-TH', { 
                                year: 'numeric', 
                                month: 'short', 
                                day: 'numeric' 
                              })} - ${new Date(student.internDetail.enddate || student.internDetail.end_date).toLocaleDateString('th-TH', { 
                                year: 'numeric', 
                                month: 'short', 
                                day: 'numeric' 
                              })}`
                            ) : (
                              "ช่วงเวลาฝึกงาน: ยังไม่ระบุ"
                            )}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </>
        )}
      </Box>
    </Drawer>
  );
};

export default StudentListDrawer;
